/**
 * AssetWorkflowRulesPage.jsx
 *
 * Settings page for managing Asset Service Workflow Rules.
 * Rules define what happens automatically at each lifecycle stage of a service record:
 *   - OnScheduled  — when a service schedule creates its reminder Action Item
 *   - OnSubmitted  — when a technician submits job cards for review
 *   - OnApproved   — when Procurement approves the service record
 *   - OnRejected   — when Procurement rejects the service record
 *
 * Each rule can:
 *   - Reassign the Action Item to a configured department and/or user
 *   - Override the Action Item template key
 *   - Send an optional notification email to the new assignee
 *
 * Rules with no ServiceType set are "catch-all" rules that fire for every service type.
 *
 * @module SettingsPages/AssetWorkflowRulesPage
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
    FaCogs, FaPlus, FaEdit, FaTrashAlt, FaSpinner,
    FaTimes, FaSave, FaBell, FaExclamationTriangle, FaProjectDiagram
} from 'react-icons/fa';
import '@/styles/ModuleTemplateConfigStyles.scss';
import * as Sentry from '@sentry/react';

const TRIGGER_LABELS = {
    OnScheduled: 'On Scheduled',
    OnSubmitted:  'On Submitted',
    OnApproved:   'On Approved',
    OnRejected:   'On Rejected'
};

const emptyForm = () => ({
    name: '',
    description: '',
    assetServiceTypeId: '',
    triggerStage: 'OnSubmitted',
    assignToDepartmentId: '',
    reassignToUserId: '',
    sendNotification: true,
    notificationEmailTemplateId: '',
    notificationMessage: '',
    sortOrder: 0
});

/**
 * AssetWorkflowRulesPage
 */
const AssetWorkflowRulesPage = () => {
    const { apiFetch } = useAuth();

    const [rules, setRules] = useState([]);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [triggerStages, setTriggerStages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    // ── Data loading ─────────────────────────────────────────────────────────

    const fetchRules = useCallback(async () => {
        try {
            const res = await apiFetch('/api/AssetManagement/workflow-rules');
            if (res.ok) {
                const data = await res.json();
                setRules(data.data ?? []);
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to load workflow rules');
        }
    }, [apiFetch]);

    const fetchReferenceData = useCallback(async () => {
        try {
            const res = await apiFetch('/api/AssetManagement/workflow-rules/reference-data');
            if (res.ok) {
                const data = await res.json();
                setDepartments(data.data?.departments ?? []);
                setUsers(data.data?.users ?? []);
                setEmailTemplates(data.data?.emailTemplates ?? []);
                setTriggerStages(data.data?.triggerStages ?? []);
            }
        } catch (err) {
            Sentry.captureException(err);
        }
    }, [apiFetch]);

    const fetchServiceTypes = useCallback(async () => {
        try {
            const res = await apiFetch('/api/AssetManagement/service-types');
            if (res.ok) {
                const data = await res.json();
                setServiceTypes(data.data ?? []);
            }
        } catch (err) {
            Sentry.captureException(err);
        }
    }, [apiFetch]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        Promise.all([fetchRules(), fetchReferenceData(), fetchServiceTypes()])
            .finally(() => setLoading(false));
    }, [fetchRules, fetchReferenceData, fetchServiceTypes]);

    // ── Modal helpers ────────────────────────────────────────────────────────

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm());
        setShowModal(true);
    };

    const openEdit = (rule) => {
        setEditingId(rule.assetServiceWorkflowRuleId);
        setForm({
            name: rule.name ?? '',
            description: rule.description ?? '',
            assetServiceTypeId: rule.assetServiceTypeId ?? '',
            triggerStage: rule.triggerStage ?? 'OnSubmitted',
            assignToDepartmentId: rule.assignToDepartmentId ?? '',
            reassignToUserId: rule.reassignToUserId ?? '',
            sendNotification: rule.sendNotification ?? true,
            notificationEmailTemplateId: rule.notificationEmailTemplateId ?? '',
            notificationMessage: rule.notificationMessage ?? '',
            sortOrder: rule.sortOrder ?? 0
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm(emptyForm());
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    // ── Save ─────────────────────────────────────────────────────────────────

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error('Rule name is required');
            return;
        }
        if (!form.triggerStage) {
            toast.error('Trigger stage is required');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                assetServiceTypeId: form.assetServiceTypeId !== '' ? parseInt(form.assetServiceTypeId, 10) : null,
                triggerStage: form.triggerStage,
                assignToDepartmentId: form.assignToDepartmentId !== '' ? parseInt(form.assignToDepartmentId, 10) : null,
                reassignToUserId: form.reassignToUserId !== '' ? parseInt(form.reassignToUserId, 10) : null,
                sendNotification: form.sendNotification,
                notificationEmailTemplateId: form.notificationEmailTemplateId !== '' ? parseInt(form.notificationEmailTemplateId, 10) : null,
                notificationMessage: form.notificationMessage.trim() || null,
                sortOrder: parseInt(form.sortOrder, 10) || 0
            };

            const url = editingId
                ? `/api/AssetManagement/workflow-rules/${editingId}`
                : '/api/AssetManagement/workflow-rules';
            const method = editingId ? 'PUT' : 'POST';

            const res = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                toast.success(editingId ? 'Workflow rule updated' : 'Workflow rule created');
                closeModal();
                await fetchRules();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.message ?? 'Failed to save workflow rule');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('An unexpected error occurred');
        } finally {
            setSaving(false);
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────

    const handleDelete = async (ruleId) => {
        try {
            const res = await apiFetch(`/api/AssetManagement/workflow-rules/${ruleId}`, { method: 'DELETE' });
            if (res.ok) {
                toast.success('Workflow rule removed');
                setDeleteConfirmId(null);
                await fetchRules();
            } else {
                toast.error('Failed to remove workflow rule');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('An unexpected error occurred');
        }
    };

    // ── Helpers ───────────────────────────────────────────────────────────────

    const getServiceTypeName = (id) => {
        if (!id) return <span style={{ color: '#aaa', fontStyle: 'italic' }}>All types</span>;
        const st = serviceTypes.find(t => t.assetServiceTypeId === id);
        return st
            ? <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: st.colour ?? '#aaa', marginRight: 6 }} />{st.name}</span>
            : `#${id}`;
    };

    const getDepartmentName = (id) => {
        if (!id) return '—';
        const d = departments.find(d => d.departmentID === id);
        return d?.departmentName ?? `#${id}`;
    };

    const getUserName = (id) => {
        if (!id) return '—';
        const u = users.find(u => u.userID === id);
        return u?.name ?? `#${id}`;
    };

    // ── Render ────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="module-template-config loading-state">
                <FaSpinner className="spin" /> Loading workflow rules&hellip;
            </div>
        );
    }

    return (
        <div className="module-template-config">
            <div className="config-header">
                <div className="config-title">
                    <FaProjectDiagram className="config-icon" />
                    <div>
                        <h2>Asset Service Workflow Rules</h2>
                        <p>Define automatic routing and notification rules that fire at each stage of an asset service lifecycle.</p>
                    </div>
                </div>
                <button className="btn-primary" onClick={openCreate}>
                    <FaPlus /> New Rule
                </button>
            </div>

            {rules.length === 0 ? (
                <div className="empty-state">
                    <FaCogs size={40} />
                    <p>No workflow rules configured yet.</p>
                    <p>Create a rule to automatically route Action Items when service records are submitted, approved, or rejected.</p>
                </div>
            ) : (
                <table className="config-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Service Type</th>
                            <th>Trigger</th>
                            <th>Assign To Dept</th>
                            <th>Reassign To User</th>
                            <th>Notify</th>
                            <th style={{ width: 80 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rules.map(rule => (
                            <tr key={rule.assetServiceWorkflowRuleId}>
                                <td>
                                    <strong>{rule.name}</strong>
                                    {rule.description && <div className="row-sub">{rule.description}</div>}
                                </td>
                                <td>{getServiceTypeName(rule.assetServiceTypeId)}</td>
                                <td>
                                    <span className={`badge badge-trigger badge-${rule.triggerStage?.toLowerCase()}`}>
                                        {TRIGGER_LABELS[rule.triggerStage] ?? rule.triggerStage}
                                    </span>
                                </td>
                                <td>{getDepartmentName(rule.assignToDepartmentId)}</td>
                                <td>{getUserName(rule.reassignToUserId)}</td>
                                <td>{rule.sendNotification ? <FaBell title="Notification enabled" style={{ color: '#f63040' }} /> : '—'}</td>
                                <td className="actions-cell">
                                    <button className="btn-icon" title="Edit" onClick={() => openEdit(rule)}>
                                        <FaEdit />
                                    </button>
                                    {deleteConfirmId === rule.assetServiceWorkflowRuleId ? (
                                        <>
                                            <button className="btn-icon btn-danger" title="Confirm delete" onClick={() => handleDelete(rule.assetServiceWorkflowRuleId)}>
                                                <FaExclamationTriangle />
                                            </button>
                                            <button className="btn-icon" title="Cancel" onClick={() => setDeleteConfirmId(null)}>
                                                <FaTimes />
                                            </button>
                                        </>
                                    ) : (
                                        <button className="btn-icon btn-danger" title="Remove" onClick={() => setDeleteConfirmId(rule.assetServiceWorkflowRuleId)}>
                                            <FaTrashAlt />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* ── Create / Edit Modal ── */}
            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-panel" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingId ? 'Edit Workflow Rule' : 'New Workflow Rule'}</h3>
                            <button className="btn-icon" onClick={closeModal}><FaTimes /></button>
                        </div>

                        <form className="modal-body" onSubmit={handleSave}>
                            {/* Name */}
                            <div className="form-group">
                                <label htmlFor="wfr-name">Rule Name <span className="required">*</span></label>
                                <input
                                    id="wfr-name"
                                    name="name"
                                    type="text"
                                    className="form-control"
                                    value={form.name}
                                    onChange={handleChange}
                                    placeholder="e.g. Route to Procurement on submission"
                                    required
                                />
                            </div>

                            {/* Description */}
                            <div className="form-group">
                                <label htmlFor="wfr-description">Description</label>
                                <textarea
                                    id="wfr-description"
                                    name="description"
                                    className="form-control"
                                    value={form.description}
                                    onChange={handleChange}
                                    rows={2}
                                    placeholder="Optional description of what this rule does"
                                />
                            </div>

                            <div className="form-row">
                                {/* Service Type */}
                                <div className="form-group">
                                    <label htmlFor="wfr-serviceType">Service Type</label>
                                    <select
                                        id="wfr-serviceType"
                                        name="assetServiceTypeId"
                                        className="form-control"
                                        value={form.assetServiceTypeId}
                                        onChange={handleChange}
                                    >
                                        <option value="">All service types</option>
                                        {serviceTypes.map(st => (
                                            <option key={st.assetServiceTypeId} value={st.assetServiceTypeId}>
                                                {st.name}
                                            </option>
                                        ))}
                                    </select>
                                    <small>Leave blank to apply this rule to every service type.</small>
                                </div>

                                {/* Trigger Stage */}
                                <div className="form-group">
                                    <label htmlFor="wfr-trigger">Trigger Stage <span className="required">*</span></label>
                                    <select
                                        id="wfr-trigger"
                                        name="triggerStage"
                                        className="form-control"
                                        value={form.triggerStage}
                                        onChange={handleChange}
                                        required
                                    >
                                        {(triggerStages.length > 0 ? triggerStages : Object.keys(TRIGGER_LABELS)).map(stage => (
                                            <option key={stage} value={stage}>
                                                {TRIGGER_LABELS[stage] ?? stage}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                {/* Assign To Department */}
                                <div className="form-group">
                                    <label htmlFor="wfr-dept">Assign To Department</label>
                                    <select
                                        id="wfr-dept"
                                        name="assignToDepartmentId"
                                        className="form-control"
                                        value={form.assignToDepartmentId}
                                        onChange={handleChange}
                                    >
                                        <option value="">— No change —</option>
                                        {departments.map(d => (
                                            <option key={d.departmentID} value={d.departmentID}>
                                                {d.departmentName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Reassign To User */}
                                <div className="form-group">
                                    <label htmlFor="wfr-user">Reassign To User</label>
                                    <select
                                        id="wfr-user"
                                        name="reassignToUserId"
                                        className="form-control"
                                        value={form.reassignToUserId}
                                        onChange={handleChange}
                                    >
                                        <option value="">— Department default —</option>
                                        {users.map(u => (
                                            <option key={u.userID} value={u.userID}>
                                                {u.name}
                                            </option>
                                        ))}
                                    </select>
                                    <small>If blank, the department&rsquo;s default assignee is used.</small>
                                </div>
                            </div>

                            <div className="form-row">
                                {/* Sort Order */}
                                <div className="form-group" style={{ maxWidth: 120 }}>
                                    <label htmlFor="wfr-sort">Sort Order</label>
                                    <input
                                        id="wfr-sort"
                                        name="sortOrder"
                                        type="number"
                                        className="form-control"
                                        value={form.sortOrder}
                                        onChange={handleChange}
                                        min={0}
                                    />
                                </div>

                                {/* Send Notification */}
                                <div className="form-group form-group--checkbox">
                                    <label>
                                        <input
                                            name="sendNotification"
                                            type="checkbox"
                                            checked={form.sendNotification}
                                            onChange={handleChange}
                                        />
                                        Send notification email to new assignee
                                    </label>
                                </div>
                            </div>

                            {/* Notification Message */}
                            {form.sendNotification && (
                                <>
                                    {/* Email Template */}
                                    <div className="form-group">
                                        <label htmlFor="wfr-email-template">Notification Email Template</label>
                                        <select
                                            id="wfr-email-template"
                                            name="notificationEmailTemplateId"
                                            className="form-control"
                                            value={form.notificationEmailTemplateId}
                                            onChange={handleChange}
                                        >
                                            <option value="">— Use default (fallback) —</option>
                                            {Object.entries(
                                                emailTemplates.reduce((acc, t) => {
                                                    const cat = t.category || 'General';
                                                    (acc[cat] = acc[cat] || []).push(t);
                                                    return acc;
                                                }, {})
                                            ).map(([cat, items]) => (
                                                <optgroup key={cat} label={cat}>
                                                    {items.map(t => (
                                                        <option key={t.emailTemplateId} value={t.emailTemplateId}>
                                                            {t.displayName}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            ))}
                                        </select>
                                        <small>Select a branded email template for this rule’s notification. Leave blank to use the built-in fallback.</small>
                                    </div>

                                    {/* Custom notification message (only shown when no template is selected) */}
                                    {!form.notificationEmailTemplateId && (
                                        <div className="form-group">
                                            <label htmlFor="wfr-notif">Custom Notification Message</label>
                                            <textarea
                                                id="wfr-notif"
                                                name="notificationMessage"
                                                className="form-control"
                                                value={form.notificationMessage}
                                                onChange={handleChange}
                                                rows={3}
                                                placeholder="Leave blank to use the default notification template"
                                            />
                                        </div>
                                    )}
                                </>
                            )}

                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary" disabled={saving}>
                                    {saving ? <><FaSpinner className="spin" /> Saving&hellip;</> : <><FaSave /> Save Rule</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssetWorkflowRulesPage;
